import React, { useState, useRef, useEffect, useMemo } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { putClient } from "../API/clientAPI";
import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";

import AreasTable from "./FormComponents/AreasTable";
import StepHeaderCard from "./FormComponents/StepHeaderCard";
import StepActionBar from "./FormComponents/StepActionBar";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import QcServiceDetailsSection from "./FormComponents/QcServiceDetailsSection";
import QcFindingsRecommendationsSection from "./FormComponents/QcFindingsRecommendationsSection";
import QcCustomerResponsibilitiesSection from "./FormComponents/QcCustomerResponsibilitiesSection";
import QcChemicalControlSection from "./FormComponents/QcChemicalControlSection";
import TechnicianSignatureCard from "./FormComponents/TechnicianSignatureCard";
import EditTechniciansDialog from "./FormComponents/EditTechniciansDialog";
import ClientSignatureCard from "./FormComponents/ClientSignatureCard";
import ClientSatisfactionCard from "./FormComponents/ClientSatisfactionCard";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";

import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function QcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    chemical_used: [],
    job_date: "",
    start_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState();
  const [links, setLinks] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  const reportGenerated = Boolean(
    links?.receipts_url || links?.certificate_url || links?.reports_url
  );

  const STEP_INDEX = {
    REVIEW_JOB_DETAILS: 0,
    QC_SERVICE_DETAILS: 1,
    AREAS_TREATED: 2,
    FINDINGS_AND_RECOMMENDATIONS: 3,
    CUSTOMER_RESPONSIBILITIES: 4,
    CHEMICAL_AND_CONTROL: 5,
    TECHNICIAN_ONLY: 6,
    CLIENT_SIGNATURE: 7,
    CLIENT_SATISFACTION: 8,
    FINAL_REVIEW: 9,
  };

  const handleCancel = () => {
    navigate("/manage-service-reports");
  };

  const handleClose = () => {
    navigate("/manage-service-reports");
  };

  const goBackToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onInvalid = (formErrors = {}) => {
    console.log("QcForm submit blocked by validation:", formErrors);
    setErrorMessage("Please complete the missing required fields before submitting.");

    if (
      formErrors.technician_signatory ||
      formErrors.accomplishedSignature ||
      formErrors.accomplishedUploaded
    ) {
      setCurrentStep(STEP_INDEX.TECHNICIAN_ONLY);
      return;
    }

    if (
      formErrors.acknowledgedClientSignatory ||
      formErrors.acknowledgedSignature ||
      formErrors.acknowledgedUploaded
    ) {
      setCurrentStep(STEP_INDEX.CLIENT_SIGNATURE);
      return;
    }

    if (formErrors.satisfaction_rating) {
      setCurrentStep(STEP_INDEX.CLIENT_SATISFACTION);
      return;
    }

    if (formErrors.control_against_count || formErrors.control_against_others_text) {
      setCurrentStep(STEP_INDEX.CHEMICAL_AND_CONTROL);
      return;
    }

    if (formErrors.findingsAreas || formErrors.recommendations) {
      setCurrentStep(STEP_INDEX.FINDINGS_AND_RECOMMENDATIONS);
      return;
    }

    if (formErrors.areas_treated_count) {
      setCurrentStep(STEP_INDEX.AREAS_TREATED);
      return;
    }

    if (
      formErrors.nature_of_work_count ||
      formErrors.structure_type_count ||
      formErrors.gpc_count ||
      formErrors.action_taken_count ||
      formErrors.nature_of_work_others_text ||
      formErrors.action_taken_others_text
    ) {
      setCurrentStep(STEP_INDEX.QC_SERVICE_DETAILS);
      return;
    }

    if (formErrors.status) {
      setCurrentStep(STEP_INDEX.REVIEW_JOB_DETAILS);
    }
  };

  const buildStepLogEntry = (stepKey, stepLabel, timestamp) => ({
    step_key: stepKey,
    step_label: stepLabel,
    action: "NEXT_CLICKED",
    timestamp,
  });

  const logReportStep = async (stepKey, stepLabel) => {
    if (!jobOrder?.id) {
      throw new Error("Missing job order id.");
    }

    const timestamp = new Date().toISOString();

    const existingLogs = Array.isArray(jobOrder?.report_page_logs)
      ? jobOrder.report_page_logs
      : [];

    const nextLogs = [
      ...existingLogs,
      buildStepLogEntry(stepKey, stepLabel, timestamp),
    ];

    const payload = {
      report_page_logs: nextLogs,
      [`${stepKey}_completed_at`]: timestamp,
    };

    await putJobOrder(jobOrder.id, payload);

    setJobOrder((prev) => ({
      ...prev,
      ...payload,
    }));

    return timestamp;
  };

  const goToStep = async ({
    currentStepKey,
    currentStepLabel,
    nextStepIndex,
    fieldsToValidate = [],
    validationMessage = "Please complete the required fields before proceeding.",
  }) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (fieldsToValidate.length > 0) {
        const isValid = await trigger(fieldsToValidate);

        if (!isValid) {
          setErrorMessage(validationMessage);
          setIsLoading(false);
          onInvalid();
          return;
        }
      }

      await logReportStep(currentStepKey, currentStepLabel);

      setCurrentStep(nextStepIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(`Failed to log step: ${currentStepKey}`, error);
      setErrorMessage("Failed to proceed to the next page. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNextFromReviewJobDetails = async () => {
    await goToStep({
      currentStepKey: "review_job_details",
      currentStepLabel: "REVIEW JOB DETAILS",
      nextStepIndex: STEP_INDEX.QC_SERVICE_DETAILS,
      fieldsToValidate: ["status"],
      validationMessage: "Please complete the required fields before proceeding.",
    });
  };

  const handleNextFromQcServiceDetails = async () => {
    await goToStep({
      currentStepKey: "service_details",
      currentStepLabel: "SERVICE DETAILS",
      nextStepIndex: STEP_INDEX.AREAS_TREATED,
      fieldsToValidate: [
        "nature_of_work_count",
        "structure_type_count",
        "gpc_count",
        "action_taken_count",
        "nature_of_work_others_text",
        "action_taken_others_text",
      ],
      validationMessage: "Please complete all required Page 2 sections before proceeding.",
    });
  };

  const handleNextFromAreasTreated = async () => {
    await goToStep({
      currentStepKey: "areas_treated",
      currentStepLabel: "AREAS TREATED",
      nextStepIndex: STEP_INDEX.FINDINGS_AND_RECOMMENDATIONS,
      fieldsToValidate: ["areas_treated_count"],
      validationMessage: "Please select at least one area treated before proceeding.",
    });
  };

  const handleNextFromFindingsAndRecommendations = async () => {
    await goToStep({
      currentStepKey: "findings_and_recommendations",
      currentStepLabel: "FINDINGS AND RECOMMENDATIONS",
      nextStepIndex: STEP_INDEX.CUSTOMER_RESPONSIBILITIES,
      fieldsToValidate: ["findingsAreas", "recommendations"],
      validationMessage: "Please add at least one valid recommendation before proceeding.",
    });
  };

  const handleNextFromCustomerResponsibilities = async () => {
    await goToStep({
      currentStepKey: "customer_responsibilities",
      currentStepLabel: "CUSTOMER RESPONSIBILITIES",
      nextStepIndex: STEP_INDEX.CHEMICAL_AND_CONTROL,
      fieldsToValidate: [],
      validationMessage: "Please complete the required fields before proceeding.",
    });
  };

  const handleNextFromChemicalAndControl = async () => {
    await goToStep({
      currentStepKey: "chemical_and_control",
      currentStepLabel: "CHEMICAL USED / CONTROL AGAINST",
      nextStepIndex: STEP_INDEX.TECHNICIAN_ONLY,
      fieldsToValidate: [
        "control_against_count",
        "control_against_others_text",
      ],
      validationMessage:
        "Please complete the required Chemical Used / Control Against section before proceeding.",
    });
  };

  const handleNextFromTechnicianOnly = async () => {
    await goToStep({
      currentStepKey: "technician_confirmation",
      currentStepLabel: "FOR TECHNICIANS ONLY",
      nextStepIndex: STEP_INDEX.CLIENT_SIGNATURE,
      fieldsToValidate: [
        "technician_signatory",
        "accomplishedSignature",
        "accomplishedUploaded",
      ],
      validationMessage: "Please complete the technician section before proceeding.",
    });
  };

  const handleNextFromClientSignature = async () => {
    await goToStep({
      currentStepKey: "client_signature",
      currentStepLabel: "FOR CLIENT ONLY - SIGNATURE",
      nextStepIndex: STEP_INDEX.CLIENT_SATISFACTION,
      fieldsToValidate: [
        "acknowledgedClientSignatory",
        "acknowledgedSignature",
        "acknowledgedUploaded",
      ],
      validationMessage: "Please complete the client signature section before proceeding.",
    });
  };

  const handleNextFromClientSatisfaction = async () => {
    await goToStep({
      currentStepKey: "client_satisfaction",
      currentStepLabel: "FOR CLIENT ONLY - SATISFACTION",
      nextStepIndex: STEP_INDEX.FINAL_REVIEW,
      fieldsToValidate: ["satisfaction_rating"],
      validationMessage: "Please complete the client satisfaction section before proceeding.",
    });
  };

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);
  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });

  const [otherControlText, setOtherControlText] = useState("");

  const RecItemSchema = z.object({
    option: z.string().min(1, "Please pick a recommendation."),
    other_text: z.string().optional(),
  }).superRefine((val, ctx) => {
    if (val.option === "Other" && !val.other_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["other_text"],
        message: "Please specify your recommendation.",
      });
    }
  });

  const FindingsAreaSchema = z.object({
    location: z
      .string()
      .trim()
      .min(1, "Please specify at least one inspected location."),
    pests: z.object({
      Roaches: z.number().optional(),
      Mosq: z.number().optional(),
      Rodents: z.number().optional(),
      Flies: z.number().optional(),
      Ants: z.number().optional(),
      Others: z.number().optional(),
    }),
  });

  const QcSchema = z.object({
    // required by your UI/submit
    satisfaction_rating: z.coerce.number()
      .min(1, "Please pick a rating (1–5).")
      .max(5, "Please pick a rating (1–5)."),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.coerce
      .boolean()
      .refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.coerce
      .boolean()
      .refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().min(1, "Please select a technician signatory."),
    acknowledgedClientSignatory: z.string().min(1, "Please enter client representative."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
      }),
    areas_treated_count: z.coerce.number().min(1, "Please select at least one area treated."),
    findings_count: z.coerce.number().optional(),
    findingsAreas: z
      .array(FindingsAreaSchema)
      .min(1, "Please specify at least one inspected location."),
    recommendations: z
      .array(RecItemSchema)
      .min(1, "Please add at least one recommendation."),
    control_against_count: z.coerce.number().min(1, "Please select at least one under Control Against."),
    // When "Others" is checked, require the text
    control_against_others: z.coerce.boolean().optional(),
    control_against_others_text: z.string().optional(),
    nature_of_work_count: z.coerce.number().min(1, "Please select at least one Nature of Work."),
    structure_type_count: z.coerce.number().min(1, "Please select at least one Type of Structure."),
    gpc_count: z.coerce.number().min(1, "Please select at least one General Pest Control option."),
    action_taken_count: z.coerce.number().min(1, "Please select at least one Action Taken / Treatment Done."),
    nature_of_work_others: z.coerce.boolean().optional(),
    nature_of_work_others_text: z.string().optional(),
    action_taken_others: z.coerce.boolean().optional(),
    action_taken_others_text: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (data.control_against_others && !data.control_against_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["control_against_others_text"],
        message: 'Please specify "Others" under Control Against.',
      });
    }

    if (data.nature_of_work_others && !data.nature_of_work_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nature_of_work_others_text"],
        message: 'Please specify "Others" under Nature of Work.',
      });
    }

    if (data.action_taken_others && !data.action_taken_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["action_taken_others_text"],
        message: 'Please specify "Others" under Action Taken / Treatment Done.',
      });
    }
  }).passthrough();


  const {
    control,
    register,
    setValue,
    handleSubmit: handleFormSubmit,
    clearErrors,
    setError,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(QcSchema),
    defaultValues: {
      // REQUIRED fields for validation
      areas_treated_count: 0,
      findings_count: 0,
      control_against_count: 0,
      control_against_others: false,
      control_against_others_text: "",
      satisfaction_rating: 0,
      client_satisfaction_remarks: "",
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      nature_of_work_count: 0,
      structure_type_count: 0,
      gpc_count: 0,
      action_taken_count: 0,
      nature_of_work_others: false,
      nature_of_work_others_text: "",
      action_taken_others: false,
      action_taken_others_text: "",
      findingsAreas: [
        {
          location: "",
          pests: {
            Roaches: 0,
            Mosq: 0,
            Rodents: 0,
            Flies: 0,
            Ants: 0,
            Others: 0,
          },
        },
      ],
      // recommendations: [{ option: "", otherText: "" }],
      recommendations: [{ option: "", other_text: "" }],
    },
  });

  // const [satisfactionRating, setSatisfactionRating] = useState(0);
  const satisfactionRating = useWatch({
    control,
    name: "satisfaction_rating",
  }) || 0;

  const satisfactionRemarks = useWatch({
    control,
    name: "client_satisfaction_remarks",
  }) || "";

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});

        const existingRating =
          Number(data?.satisfaction_rating) >= 1 &&
          Number(data?.satisfaction_rating) <= 5
            ? Number(data.satisfaction_rating)
            : 0;

        setValue("satisfaction_rating", existingRating, {
          shouldValidate: false,
          shouldDirty: false,
        });

        setValue(
          "client_satisfaction_remarks",
          data?.client_satisfaction_remarks || "",
          {
            shouldValidate: false,
            shouldDirty: false,
          }
        );
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage(`Error fetching job order: ${error?.message || "Unknown error"}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map((t) => t.id);
    setEditSelectedTechIds(ids.length ? ids : [""]);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const saveEditTechs = async () => {
    const uniqueIds = [...new Set(editSelectedTechIds.filter(Boolean))];

    const selected = (allTechnicians || [])
      .filter((t) => uniqueIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      setIsLoading(true);

      await putJobOrder(jobOrder.id, { technicians: selected });

      setJobOrder((prev) => ({
        ...prev,
        technicians: selected,
      }));

      setjobOrderParameters((prev) => ({
        ...prev,
        accomplished_by_technician: selected.map(
          (t) => `${t.firstname} ${t.lastname}`
        ),
      }));

      const validNames = new Set(
        selected.map((t) => `${t.firstname} ${t.lastname}`)
      );

      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setSuccessMessage("Technicians updated.");
      setEditTechsOpen(false);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setIsLoading(false);
    }
  };

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("")

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "" },
    { label: "2", chemical: "" },
    { label: "3", chemical: "" },
    { label: "4", chemical: "" },
    { label: "5", chemical: "" },
    { label: "6", chemical: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const controlAgainstCount = useMemo(
    () => Object.values(controlAgainst).filter(Boolean).length,
    [controlAgainst]
  );

  useEffect(() => {
    setValue("control_against_count", controlAgainstCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("control_against_others", controlAgainst.others, {
      shouldValidate: true,
    });
    setValue("control_against_others_text", otherControlText, {
      shouldValidate: true,
    });
  }, [controlAgainstCount, controlAgainst.others, otherControlText, setValue]);

  // Type of Structure (checkboxes) + side text fields
  const [structureType, setStructureType] = useState({
    residential: false,
    commercialEstablishment: false,
    others: false,
  });

  const [structureNatureOfBusiness, setStructureNatureOfBusiness] = useState("");
  const [structureOthersText, setStructureOthersText] = useState("");

  const handleStructureTypeChange = (field) => (event) => {
    const checked = event.target.checked;
    setStructureType((prev) => ({ ...prev, [field]: checked }));

    // Clear side fields if their checkbox is unchecked
    if (field === "commercialEstablishment" && !checked) setStructureNatureOfBusiness("");
    if (field === "others" && !checked) setStructureOthersText("");
  };

  // Nature of Work
  const [natureOfWork, setNatureOfWork] = useState({
    generalPestControl: false,
    disinfection: false,
    fumigation: false,
    rodentControl: false,
    foggingMisting: false,
    others: false,
  });
  const [natureOfWorkOthersText, setNatureOfWorkOthersText] = useState("");

  const handleNatureOfWorkChange = (field) => (e) => {
    const checked = e.target.checked;
    setNatureOfWork((prev) => ({ ...prev, [field]: checked }));
    if (field === "others" && !checked) setNatureOfWorkOthersText("");
  };

  // --- GPC (General Pest Control) ---
  const [gpcMain, setGpcMain] = useState({
    insects: false,    // Flying and crawling insects
    rodents: false,    // Rodents
    other: false,      // Other pests
  });

  const [gpcInsects, setGpcInsects] = useState({
    cockroach: false,
    flies: false,
    mosquitoes: false,
  });

  const [gpcOtherText, setGpcOtherText] = useState("");

  const handleGpcMainChange = (field) => (e) => {
    const checked = e.target.checked;
    setGpcMain((prev) => ({ ...prev, [field]: checked }));

    // Clear/disable dependents when toggled off
    if (field === "insects" && !checked) {
      setGpcInsects({ cockroach: false, flies: false, mosquitoes: false });
    }
    if (field === "other" && !checked) {
      setGpcOtherText("");
    }
  };

  const handleGpcInsectChange = (field) => (e) => {
    setGpcInsects((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  // --- ACTION TAKEN / TREATMENT DONE ---
  const [actionTaken, setActionTaken] = useState({
    mistSpraying: false,
    thermalFogging: false,
    coldFogging: false,
    larviciding: false,
    gelBaiting: false,
    uvLight: false,
    wiping: false,
    steaming: false,
    others: false,
  });
  const [actionTakenOthersText, setActionTakenOthersText] = useState("");

  const handleActionTakenChange = (field) => (e) => {
    const checked = e.target.checked;
    setActionTaken((prev) => ({ ...prev, [field]: checked }));
    if (field === "others" && !checked) setActionTakenOthersText("");
  };

  const natureOfWorkCount = useMemo(
    () => Object.values(natureOfWork).filter(Boolean).length,
    [natureOfWork]
  );

  const structureTypeCount = useMemo(
    () => Object.values(structureType).filter(Boolean).length,
    [structureType]
  );

  const gpcCount = useMemo(
    () => Object.values(gpcMain).filter(Boolean).length,
    [gpcMain]
  );

  const actionTakenCount = useMemo(
    () => Object.values(actionTaken).filter(Boolean).length,
    [actionTaken]
  );

  useEffect(() => {
    setValue("nature_of_work_count", natureOfWorkCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("nature_of_work_others", natureOfWork.others, {
      shouldValidate: true,
    });
    setValue("nature_of_work_others_text", natureOfWorkOthersText, {
      shouldValidate: true,
    });
  }, [natureOfWorkCount, natureOfWork.others, natureOfWorkOthersText, setValue]);

  useEffect(() => {
    setValue("structure_type_count", structureTypeCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [structureTypeCount, setValue]);

  useEffect(() => {
    setValue("gpc_count", gpcCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [gpcCount, setValue]);

  useEffect(() => {
    setValue("action_taken_count", actionTakenCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("action_taken_others", actionTaken.others, {
      shouldValidate: true,
    });
    setValue("action_taken_others_text", actionTakenOthersText, {
      shouldValidate: true,
    });
  }, [actionTakenCount, actionTaken.others, actionTakenOthersText, setValue]);

  // ----- AREAS TREATED: single table layout -----
  const AREAS_TREATED_LAYOUT = [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ];

  // 1-based index key builder: r{row}c{col}  (e.g., r1c3)
  const makeIdxKey = (r, c) => `r${r + 1}c${c + 1}`;

  // Build initial state only for non-empty cells
  const [areasTreated, setAreasTreated] = useState(() => {
    const init = {};
    AREAS_TREATED_LAYOUT.forEach((row, r) =>
      row.forEach((label, c) => {
        if (label) init[makeIdxKey(r, c)] = false;
      })
    );
    return init;
  });

  const handleAreasTreatedToggle = (key) => (e) => {
    const checked = e.target.checked;
    setAreasTreated((prev) => ({ ...prev, [key]: checked }));
    if (key === OTHER_AREAS_KEY && !checked) setOtherAreasText("");
  };

  const serializeAreasTreated = (state) =>
    Object.entries(state)
      .filter(([, checked]) => checked)
      .map(([k]) => {
        const m = k.match(/^r(\d+)c(\d+)$/);
        return m ? { row: Number(m[1]), col: Number(m[2]) } : null;
      })
      .filter(Boolean);

  const [otherAreasText, setOtherAreasText] = useState("");

  // Find the row/col of the "Other areas" cell and compute its index key (1-based)
  const OTHER_AREAS_KEY = useMemo(() => {
    for (let r = 0; r < AREAS_TREATED_LAYOUT.length; r++) {
      for (let c = 0; c < AREAS_TREATED_LAYOUT[r].length; c++) {
        if (AREAS_TREATED_LAYOUT[r][c] === "Other areas") {
          return `r${r + 1}c${c + 1}`;
        }
      }
    }
    return null;
  }, []);

  const selectedAreasCount = React.useMemo(
    () => Object.values(areasTreated).filter(Boolean).length,
    [areasTreated]
  );

  React.useEffect(() => {
    setValue("areas_treated_count", selectedAreasCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [selectedAreasCount, setValue]);

  // --- FINDINGS ---
  const PEST_TYPES = ['Flies', 'Mosquitoes', 'Roaches', 'Ants', 'Rodents'];
  const LEVELS = ['L', 'M', 'H']; // L - Light, M - Medium, H - High

  // e.g. { Flies: 'L', Mosquitoes: null, ... }
  const [infestationFindings, setInfestationFindings] = useState(
    Object.fromEntries(PEST_TYPES.map(p => [p, null]))
  );

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'common.white' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  const findingsSelectedCount = useMemo(
    () => Object.values(infestationFindings).filter(v => v === 'L' || v === 'M' || v === 'H').length,
    [infestationFindings]
  );

  useEffect(() => {
    setValue("findings_count", findingsSelectedCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  }, [findingsSelectedCount, setValue]);

  // Findings - Areas
  const { fields: findingsAreas, append: appendFindingArea, remove: removeFindingArea } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  const MAX_FINDINGS_AREAS = 6;

  // --- RECOMMENDATIONS ---
  const MAX_RECOMMENDATIONS = 8;

  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  // Dynamic list: up to 8
  const {
    fields: recFields,
    append: appendRec,
    remove: removeRec,
  } = useFieldArray({ control, name: "recommendations" });

  // Watch all recommendation rows to know when to show text fields
  const recValues = useWatch({ control, name: "recommendations" }) || [];

  useEffect(() => {
    if (recFields.length === 0) {
      // appendRec({ option: "", otherText: "" });
      appendRec({ option: "", other_text: "" });
    }
  }, [recFields.length, appendRec]);

  // --- CUSTOMER'S RESPONSIBILITIES ---
  const [customerResp, setCustomerResp] = useState({
    // SANITATION
    sanitation1: "",
    sanitation2: "",
    sanitation3: "",
    // RODENT ENTRY POINT
    sealing: "",
    barriers: "",
    repairsMaintenance: "",
    renovation: "",
  });

  const handleCustomerRespValueChange = (field, value) => {
    setCustomerResp((prev) => ({ ...prev, [field]: value }));
  };
  
  // Temporarily store the data URL
  // Save Accomplished By signature and upload to S3
  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAccomplishedSignatureURL(url);
      // ✅ RHF: uploaded URL + proof that Upload was clicked
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      // Display a success message
      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      // Handle errors and show an error message
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the Accomplished By signature without affecting the button states
  const clearAccomplishedSignature = () => {
    if (accomplishedBySignatureRef.current) {
      accomplishedBySignatureRef.current.clear();
      setAccomplishedSignatureURL("");
      setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      trigger(["accomplishedSignature", "accomplishedUploaded"]);
    }
  };

  // Save Acknowledged By signature and upload to S3
  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAcknowledgedSignatureURL(url);
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };
  // Clear the Acknowledged By signature
  const clearAcknowledgedSignature = () => {
    if (acknowledgedBySignatureRef.current) {
      acknowledgedBySignatureRef.current.clear();
      setAcknowledgedSignatureURL("");
      setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    }
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  const handleSendEmail = async (attachmentLinks) => {
    try {
      const recipient = jobOrder?.client?.contact?.email?.email;

      if (!recipient) {
        console.warn("⚠️ Email notification skipped - no client email address");
        return;
      }

      const formattedDate = new Date(
        jobOrder?.job_date || jobOrder?.date || Date.now()
      ).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const htmlBody = buildServiceReportEmail({
        formattedDate,
        attachmentLinks,
      });

      const lambdaPayload = {
        from: "boaz-reminders@maestrosystemsinc.com",
        recipients: [recipient],
        subject: `Service Report for Job Order ${jobOrder?.id}`,
        body: htmlBody,
      };

      await sendEmailQueue(lambdaPayload);
    } catch (error) {
      console.error("❌ Failed to send email notification:", error);
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const selectedPositions = serializeAreasTreated(areasTreated);
    const selectedTechs = jobOrder?.technicians || [];

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      chemical_used,
      technicians: selectedTechs,
      accomplished_by_technician: selectedTechs.map(
        t => `${t.firstname} ${t.lastname}`
      ),
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      satisfaction_rating: formData.satisfaction_rating,
      client_satisfaction_remarks: formData.client_satisfaction_remarks || "",
      control_against: {
        crawling_insects: controlAgainst.crawlingInsects,
        flying_insects:   controlAgainst.flyingInsects,
        rodent_control:   controlAgainst.rodentControl,
        others:           controlAgainst.others,
        others_text:      otherControlText.trim()
      },
      nature_of_work: {
        general_pest_control: natureOfWork.generalPestControl,
        disinfection: natureOfWork.disinfection,
        fumigation: natureOfWork.fumigation,
        rodent_control: natureOfWork.rodentControl,
        fogging_misting: natureOfWork.foggingMisting,
        others: natureOfWork.others,
        others_text: natureOfWorkOthersText.trim(),
      },
      qc_structure_type: {
        residential: structureType.residential,
        commercial_establishment: structureType.commercialEstablishment,
        others: structureType.others,
        nature_of_business: structureNatureOfBusiness.trim(),
        others_text: structureOthersText.trim(),
      },
      gpc: {
        flying_and_crawling_insects: gpcMain.insects,
        rodents: gpcMain.rodents,
        other_pests: gpcMain.other,
        insects_detail: {
          cockroach: gpcInsects.cockroach,
          flies: gpcInsects.flies,
          mosquitoes: gpcInsects.mosquitoes,
        },
        other_pests_text: gpcOtherText.trim(),
      },
      action_taken_treatment_done: {
        mist_spraying: actionTaken.mistSpraying,
        thermal_fogging: actionTaken.thermalFogging,
        cold_fogging: actionTaken.coldFogging,
        larviciding: actionTaken.larviciding,
        gel_baiting: actionTaken.gelBaiting,
        uv_light: actionTaken.uvLight,
        wiping: actionTaken.wiping,
        steaming: actionTaken.steaming,
        others: actionTaken.others,
        others_text: actionTakenOthersText.trim(),
      },
      areas_treated_positions: selectedPositions,
      areas_treated_other_text: otherAreasText.trim(),
      customers_responsibilities: {
        sanitation: [customerResp.sanitation1, customerResp.sanitation2, customerResp.sanitation3],
        rodent_entry_point: {
          sealing: customerResp.sealing,
          barriers: customerResp.barriers          
        },
        repairs_maintenance: customerResp.repairsMaintenance,
        renovation: customerResp.renovation,
      },
      findings_degree_of_infestation: {
        flies: infestationFindings.Flies,
        mosquitoes: infestationFindings.Mosquitoes,
        roaches: infestationFindings.Roaches,
        ants: infestationFindings.Ants,
        rodents: infestationFindings.Rodents,
      },
      findings_areas: formData.findingsAreas || [],
      recommendations: formData.recommendations || [],
      service_report_status: "Done",
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      const generatedLinks = buildGeneratedDocumentLinks(jobOrder.id, {
        includeReport: false,
        includeCertificate: true,
        includeReceipt: true,
      });

      setLinks(generatedLinks);
      // Send the email after updating links

      setSuccessMessage(
        "Service Report and documents generated successfully."
      );

      setIsLoading(false);
      await handleSendEmail(generatedLinks);

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
      // All parameters saved successfully
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }
  }


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Page 1 - Review Job Details */}
          {currentStep === STEP_INDEX.REVIEW_JOB_DETAILS && (
            <>
              <StepHeaderCard
                title="Review Job Details"
                subtitle="Review the job details before proceeding."
              />

              <PageOneReviewSection
                jobOrder={jobOrder}
                client={client}
                selectedJobTypes={selectedJobTypes}
                selectedJobDescriptions={selectedJobDescriptions}
                jobOrderParameters={jobOrderParameters}
                errors={errors}
                onStatusChange={(value) => handleJObOrderChange("status", value)}
                showStatus={true}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={true}
                showNext={true}
                onCancel={handleCancel}
                onNext={handleNextFromReviewJobDetails}
              />
            </>
          )}

          {/* Page 2 - QC Service Details */}
          {currentStep === STEP_INDEX.QC_SERVICE_DETAILS && (
            <>
              <StepHeaderCard
                title="QC Service Details"
                subtitle="Complete nature of work, structure type, pest coverage, and action taken."
              />

              <QcServiceDetailsSection
                mode="edit"
                natureOfWork={natureOfWork}
                natureOfWorkOthersText={natureOfWorkOthersText}
                onNatureOfWorkChange={handleNatureOfWorkChange}
                onNatureOfWorkOthersTextChange={setNatureOfWorkOthersText}
                structureType={structureType}
                structureNatureOfBusiness={structureNatureOfBusiness}
                structureOthersText={structureOthersText}
                onStructureTypeChange={handleStructureTypeChange}
                onStructureNatureOfBusinessChange={setStructureNatureOfBusiness}
                onStructureOthersTextChange={setStructureOthersText}
                gpcMain={gpcMain}
                gpcInsects={gpcInsects}
                gpcOtherText={gpcOtherText}
                onGpcMainChange={handleGpcMainChange}
                onGpcInsectChange={handleGpcInsectChange}
                onGpcOtherTextChange={setGpcOtherText}
                actionTaken={actionTaken}
                actionTakenOthersText={actionTakenOthersText}
                onActionTakenChange={handleActionTakenChange}
                onActionTakenOthersTextChange={setActionTakenOthersText}
                errors={errors}
              />

              <input type="hidden" {...register("nature_of_work_count")} />
              <input type="hidden" {...register("structure_type_count")} />
              <input type="hidden" {...register("gpc_count")} />
              <input type="hidden" {...register("action_taken_count")} />
              <input type="hidden" {...register("nature_of_work_others")} />
              <input type="hidden" {...register("nature_of_work_others_text")} />
              <input type="hidden" {...register("action_taken_others")} />
              <input type="hidden" {...register("action_taken_others_text")} />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.REVIEW_JOB_DETAILS)}
                onCancel={handleCancel}
                onNext={handleNextFromQcServiceDetails}
              />
            </>
          )}

          {/* Page 3 - Areas Treated */}
          {currentStep === STEP_INDEX.AREAS_TREATED && (
            <>
              <StepHeaderCard
                title="Areas Treated"
                subtitle="Select the treated areas before proceeding."
              />

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                        AREAS TREATED
                      </Typography>
                    }
                    sx={{ pb: 0, px: 2, pt: 2 }}
                  />
                  <CardContent sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                      <AreasTable
                        layout={AREAS_TREATED_LAYOUT}
                        state={areasTreated}
                        onToggle={handleAreasTreatedToggle}
                        ariaLabel="areas_treated"
                        getKey={(rIdx, cIdx) => makeIdxKey(rIdx, cIdx)}
                      />

                      {OTHER_AREAS_KEY && (
                        <TextField
                          label="Please specify (Other areas)"
                          size="small"
                          fullWidth
                          sx={{ mt: 1 }}
                          disabled={!areasTreated[OTHER_AREAS_KEY]}
                          value={otherAreasText}
                          onChange={(e) => setOtherAreasText(e.target.value)}
                        />
                      )}
                    </Box>

                    {errors?.areas_treated_count && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.areas_treated_count.message}
                      </FormHelperText>
                    )}

                    <input
                      type="hidden"
                      {...register("areas_treated_count", {
                        validate: (v) => Number(v) >= 1 || "Please select at least one area treated.",
                      })}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.QC_SERVICE_DETAILS)}
                onCancel={handleCancel}
                onNext={handleNextFromAreasTreated}
              />
            </>
          )}

          {/* Page 4 - Findings and Recommendations */}
          {currentStep === STEP_INDEX.FINDINGS_AND_RECOMMENDATIONS && (
            <>
              <StepHeaderCard
                title="Findings and Recommendations"
                subtitle="Record findings if applicable, then provide recommendations."
              />

              <QcFindingsRecommendationsSection
                mode="edit"
                control={control}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                pestTypes={PEST_TYPES}
                levels={LEVELS}
                infestationFindings={infestationFindings}
                onInfestationChange={(pest, value) =>
                  setInfestationFindings((prev) => ({ ...prev, [pest]: value }))
                }
                ratingBtnSx={ratingBtnSx}
                findingsAreas={findingsAreas}
                appendFindingArea={appendFindingArea}
                removeFindingArea={removeFindingArea}
                maxFindingsAreas={MAX_FINDINGS_AREAS}
                recFields={recFields}
                recValues={recValues}
                appendRec={appendRec}
                removeRec={removeRec}
                recommendationOptions={RECOMMENDATION_OPTIONS}
                maxRecommendations={MAX_RECOMMENDATIONS}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.AREAS_TREATED)}
                onCancel={handleCancel}
                onNext={handleNextFromFindingsAndRecommendations}
              />
            </>
          )}

          {/* Page 5 - Customer's Responsibilities */}
          {currentStep === STEP_INDEX.CUSTOMER_RESPONSIBILITIES && (
            <>
              <StepHeaderCard
                title="Customer's Responsibilities"
                subtitle="Record sanitation, rodent entry points, repairs, and renovation notes."
              />

              <QcCustomerResponsibilitiesSection
                mode="edit"
                value={customerResp}
                onChange={handleCustomerRespValueChange}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.FINDINGS_AND_RECOMMENDATIONS)}
                onCancel={handleCancel}
                onNext={handleNextFromCustomerResponsibilities}
              />
            </>
          )}

          {/* Page 6 - Chemical and Control */}
          {currentStep === STEP_INDEX.CHEMICAL_AND_CONTROL && (
            <>
              <StepHeaderCard
                title="Chemical Used and Control Against"
                subtitle="Record the chemicals used and select the applicable control-against options."
              />

              <QcChemicalControlSection
                mode="edit"
                chemicalUsed={chemical_used}
                onChemicalChange={handleChemicalInputChange}
                controlAgainst={controlAgainst}
                onControlAgainstChange={handleControlAgainstChange}
                otherControlText={otherControlText}
                onOtherControlTextChange={setOtherControlText}
                errors={errors}
              />

              <input type="hidden" {...register("control_against_count")} />
              <input type="hidden" {...register("control_against_others")} />
              <input type="hidden" {...register("control_against_others_text")} />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.CUSTOMER_RESPONSIBILITIES)}
                onCancel={handleCancel}
                onNext={handleNextFromChemicalAndControl}
              />
            </>
          )}
          
          {/* Page 7 - Technician Only */}
          {currentStep === STEP_INDEX.TECHNICIAN_ONLY && (
            <>
              <StepHeaderCard
                title="For Technicians Only"
                subtitle="Confirm the technician roster and sign the form."
              />

              <Grid container spacing={2}>
                <TechnicianSignatureCard
                  accomplishedBySignatureRef={accomplishedBySignatureRef}
                  canvasWidth={canvasWidth}
                  ackHeight={ackHeight}
                  technicianSignatory={technicianSignatory}
                  setTechnicianSignatory={setTechnicianSignatory}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                  jobOrder={jobOrder}
                  clearAccomplishedSignature={clearAccomplishedSignature}
                  saveAndUploadAccomplishedSignature={saveAndUploadAccomplishedSignature}
                  isLoading={isLoading}
                  isTechnician={isTechnician}
                  technicianSignatureUploadedAt={technicianSignatureUploadedAt}
                  register={register}
                  openEditTechs={openEditTechs}
                  showSchedule={true}
                  readOnlySchedule={true}
                  jobDate={jobOrderParameters?.job_date || ""}
                  startTime={jobOrderParameters?.start_time || ""}
                  onJobDateChange={(value) => handleJObOrderChange("job_date", value)}
                  onStartTimeChange={(value) => handleJObOrderChange("start_time", value)}
                  allowEditTechnicians={true}
                />
              </Grid>

              <Controller
                name="accomplishedSignature"
                control={control}
                render={() => null}
              />
              <Controller
                name="accomplishedUploaded"
                control={control}
                render={() => null}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={true}
                showCancel={true}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.CHEMICAL_AND_CONTROL)}
                onCancel={handleCancel}
                onNext={handleNextFromTechnicianOnly}
              />
            </>
          )}

          <EditTechniciansDialog
            open={editTechsOpen}
            onClose={closeEditTechs}
            onSave={saveEditTechs}
            editedTechs={editSelectedTechIds}
            techniciansList={allTechnicians}
            onChangeTech={(index, techId) => {
              setEditSelectedTechIds((prev) => {
                const next = [...prev];
                next[index] = techId;
                return next;
              });
            }}
            onAddTech={() => {
              setEditSelectedTechIds((prev) => [...prev, ""]);
            }}
            onRemoveTech={(index) => {
              setEditSelectedTechIds((prev) => prev.filter((_, i) => i !== index));
            }}
            isLoading={isLoading}
          />

          {/* Page 8 - Client Signature */}
          {currentStep === STEP_INDEX.CLIENT_SIGNATURE && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Please sign the form and provide your satisfaction rating on the next page."
              />

              <ClientSignatureCard
                acknowledgedClientSignatory={acknowledgedClientSignatory}
                setAcknowledgedClientSignatory={setAcknowledgedClientSignatory}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                acknowledgedBySignatureRef={acknowledgedBySignatureRef}
                canvasWidth={canvasWidth}
                ackHeight={ackHeight}
                saveAndUploadAcknowledgedSignature={saveAndUploadAcknowledgedSignature}
                clearAcknowledgedSignature={clearAcknowledgedSignature}
                isLoading={isLoading}
                isTechnician={isTechnician}
                clientSignatureUploadedAt={clientSignatureUploadedAt}
              />

              <Controller
                name="acknowledgedSignature"
                control={control}
                render={() => null}
              />
              <Controller
                name="acknowledgedUploaded"
                control={control}
                render={() => null}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.TECHNICIAN_ONLY)}
                onNext={handleNextFromClientSignature}
              />
            </>
          )}

          {/* Page 9 - Client Satisfaction */}
          {currentStep === STEP_INDEX.CLIENT_SATISFACTION && (
            <>
              <StepHeaderCard
                title="For Client Only"
                subtitle="Your satisfaction and feedback are important to us."
              />

              <ClientSatisfactionCard
                rating={satisfactionRating}
                remarks={satisfactionRemarks}
                onRatingChange={(val) => {
                  const next = satisfactionRating === val ? 0 : val;
                  setValue("satisfaction_rating", next, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  clearErrors("satisfaction_rating");
                }}
                onRemarksChange={(val) => {
                  setValue("client_satisfaction_remarks", val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                ratingError={errors?.satisfaction_rating?.message}
              />

              <Controller
                name="satisfaction_rating"
                control={control}
                render={() => null}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={false}
                showNext={true}
                onBack={() => goBackToStep(STEP_INDEX.CLIENT_SIGNATURE)}
                onNext={handleNextFromClientSatisfaction}
              />
            </>
          )}

          {/* Page 10 - Final Review */}
          {currentStep === STEP_INDEX.FINAL_REVIEW && (
            <>
              <StepHeaderCard
                title="Final Review & Submit"
                subtitle="Submit the report and review the generated document links."
              />

              <DocumentLinksCard
                title="Generated Documents"
                links={links}
                linkLabels={{
                  receipts_url: "Receipts.pdf",
                  certificate_url: "Certificate.pdf",
                }}
                emptyMessage="No document links available yet. Submit the report first."
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={!reportGenerated}
                showSubmit={!reportGenerated}
                showClose={reportGenerated}            
                onCancel={handleCancel}
                onClose={handleClose}
              />
            </>
          )}          
        </Grid>
        {/* Hidden validators for signatures + rating */}
        <Controller name="acknowledgedSignature" control={control}
          rules={{ required: "Please click Upload for Acknowledged signature." }}
          render={() => null}
        />
        <Controller name="acknowledgedUploaded" control={control}
          rules={{ validate: v => v === true || "Please click Upload for Acknowledged signature." }}
          render={() => null}
        />
        <Controller name="accomplishedSignature" control={control}
          rules={{ required: "Please click Upload for Accomplished signature." }}
          render={() => null}
        />
        <Controller name="accomplishedUploaded" control={control}
          rules={{ validate: v => v === true || "Please click Upload for Accomplished signature." }}
          render={() => null}
        />
        <Controller name="satisfaction_rating" control={control}
          rules={{
            required: "Please pick a rating (1–5).",
            validate: (v) => Number(v) >= 1 || "Please pick a rating (1–5).",
          }}
          render={() => null}
        />
        <Controller
          name="technician_signatory"
          control={control}
          rules={{ required: "Please choose a technician signatory." }}
          render={() => null}
        />
        <Controller
          name="acknowledgedClientSignatory"
          control={control}
          rules={{ required: "Please enter the client representative." }}
          render={() => null}
        />
      </form>
    </BaseFormWrapper>
  );
}

export default QcForm;
